<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { api, extractError } from '@/api/client'
import { useKeysStore } from '@/stores/keys'
import Modal from '@/components/ui/Modal.vue'
import LocationDisplay from '@/components/ui/LocationDisplay.vue'
import { flagSvgUrl } from '@/utils/countries'
import type {
  ActivationResponse,
  PickServerKeyResponse,
  PickServerOption,
  PickServerOptionsResponse,
  RenewalCandidate,
} from '@/types'

const router = useRouter()
const keys = useKeysStore()
const { t } = useI18n()

const code = ref('')
const status = ref<'idle' | 'processing' | 'success' | 'error'>('idle')
const message = ref('')
const result = ref<ActivationResponse | null>(null)
const modalOpen = ref(false)
const keysSyncing = ref(false)

const pickDone = ref(false)
const renewalDone = ref(false)
const serverOptions = ref<PickServerOption[]>([])
const selectedServerId = ref<number | null>(null)
const selectedCandidateKeyId = ref<number | null>(null)
const serversLoading = ref(false)
const pickError = ref('')
const pickSubmitting = ref(false)

// Послайдный (wizard) флоу:
//  1 = выбор сервера, 2 = подтверждение. Шаг 0 = обычный успех/итог.
const wizardStep = ref<0 | 1 | 2>(0)

const needsPickServer = computed(
  () => !!result.value?.pick_server && !pickDone.value,
)
const needsRenewalPick = computed(
  () =>
    !!result.value?.renewal_pick
    && !!result.value?.candidates?.length
    && !renewalDone.value,
)
const needsWizard = computed(() => needsPickServer.value || needsRenewalPick.value)

const isResume = computed(
  () => result.value?.was_already_activated === true && !needsWizard.value,
)
const isExtendedExisting = computed(
  () => result.value?.extended_existing_keys === true && !needsWizard.value,
)

const renewalCandidates = computed<RenewalCandidate[]>(
  () => result.value?.candidates ?? [],
)

const selectedServer = computed<PickServerOption | null>(
  () => serverOptions.value.find((s) => s.id === selectedServerId.value) ?? null,
)

const selectedCandidate = computed<RenewalCandidate | null>(
  () =>
    renewalCandidates.value.find((c) => c.key_id === selectedCandidateKeyId.value) ?? null,
)

const flagUrl = computed(() => {
  const si = displayServerInfo.value
  if (!si) return ''
  return flagSvgUrl(si.country_code, si.flag_code, si.location)
})

const modalTitle = computed(() => {
  if (needsRenewalPick.value) {
    return wizardStep.value === 2
      ? t('activate.modal.renewal_confirm_title')
      : t('activate.modal.renewal_pick_title')
  }
  if (needsPickServer.value) {
    return wizardStep.value === 2
      ? t('activate.modal.confirm_title')
      : t('activate.modal.pick_title')
  }
  if (result.value?.extended_keys_count) return t('activate.modal.renewal_success_title')
  if (isResume.value) return t('activate.modal.already_title')
  if (isExtendedExisting.value) return t('activate.modal.extended_title')
  return t('activate.modal.success_title')
})

const wizardSteps = computed(() => [
  t('activate.modal.step_activate'),
  t('activate.modal.step_pick'),
  t('activate.modal.step_confirm'),
])

const displayServerInfo = computed(() => {
  const r = result.value
  if (!r) return null
  if (r.server_info) return r.server_info
  if (r.server_name) {
    return {
      server_id: (r as { server_id?: number }).server_id ?? 0,
      server_name: r.server_name,
      location: '',
      country_code: '',
      flag_code: '',
      protocols_count: r.new_count ?? r.updated_count ?? 0,
    }
  }
  return null
})

const canPickSubmit = computed(
  () =>
    !serversLoading.value
    && !pickSubmitting.value
    && selectedServerId.value != null
    && serverOptions.value.length > 0,
)

const canRenewalSubmit = computed(
  () =>
    !pickSubmitting.value
    && selectedCandidateKeyId.value != null
    && renewalCandidates.value.length > 0,
)

watch(
  () => modalOpen.value && needsPickServer.value,
  (open) => {
    if (open) {
      wizardStep.value = 1
      void loadServerOptions()
    }
  },
)

watch(
  () => modalOpen.value && needsRenewalPick.value,
  (open) => {
    if (open) {
      wizardStep.value = 1
      selectedCandidateKeyId.value = null
      pickError.value = ''
    }
  },
)

async function loadServerOptions() {
  serversLoading.value = true
  pickError.value = ''
  serverOptions.value = []
  selectedServerId.value = null
  try {
    const { data } = await api.get<PickServerOptionsResponse>('/api/user/pick-server-key')
    if (!data.success || !data.servers?.length) {
      pickError.value = data.message || t('activate.modal.no_servers')
      return
    }
    serverOptions.value = data.servers
    if (selectedServerId.value != null) {
      const cur = serverOptions.value.find((s) => s.id === selectedServerId.value)
      if (cur?.is_online === false) selectedServerId.value = null
    }
  } catch (e) {
    pickError.value = extractError(e, 'Не удалось загрузить список серверов')
  } finally {
    serversLoading.value = false
  }
}

function selectServer(id: number) {
  const s = serverOptions.value.find((x) => x.id === id)
  if (s?.is_online === false) return
  selectedServerId.value = id
}

function selectCandidate(keyId: number) {
  selectedCandidateKeyId.value = keyId
}

function goNextStep() {
  if (wizardStep.value !== 1) return
  if (needsRenewalPick.value && selectedCandidateKeyId.value != null) {
    wizardStep.value = 2
    return
  }
  if (needsPickServer.value && selectedServerId.value != null) {
    wizardStep.value = 2
  }
}

function goPrevStep() {
  if (wizardStep.value === 2) {
    wizardStep.value = 1
    pickError.value = ''
  }
}

async function submit() {
  const value = code.value.trim()
  if (!value) {
    message.value = 'Введите код активации'
    status.value = 'error'
    return
  }
  if (!/^[A-Za-z0-9]+$/.test(value)) {
    message.value = 'Неверный формат кода'
    status.value = 'error'
    return
  }

  status.value = 'processing'
  message.value = ''
  result.value = null
  pickDone.value = false
  renewalDone.value = false
  selectedCandidateKeyId.value = null
  wizardStep.value = 0

  try {
    const { data } = await api.post<ActivationResponse>('/api/user/subscription-activation', {
      code: value,
    })
    if (data.success) {
      result.value = data
      status.value = 'success'
      modalOpen.value = true

      if (!data.pick_server && !data.renewal_pick) {
        keysSyncing.value = true
        keys.fetch(true).finally(() => {
          keysSyncing.value = false
        })
      }
    } else {
      message.value = data.message || 'Не удалось активировать код'
      status.value = 'error'
    }
  } catch (e) {
    message.value = extractError(e, 'Не удалось активировать код')
    status.value = 'error'
  }
}

async function submitPickServer() {
  const keyCode = result.value?.activated_key_code || code.value.trim()
  const groupHash = result.value?.group_hash?.trim() || ''
  if (selectedServerId.value == null) return
  if (!groupHash && !keyCode) return
  pickSubmitting.value = true
  pickError.value = ''
  try {
    const payload: {
      server_id: number
      key_code?: string
      group_hash?: string
    } = { server_id: selectedServerId.value }
    if (groupHash) payload.group_hash = groupHash
    if (keyCode) payload.key_code = keyCode
    const { data } = await api.post<PickServerKeyResponse>('/api/user/pick-server-key', payload)
    if (!data.success) {
      pickError.value = data.message || 'Не удалось создать ключи'
      return
    }
    const prev = result.value
    if (!prev) return
    pickDone.value = true
    wizardStep.value = 0
    result.value = {
      ...prev,
      success: true,
      pick_server: false,
      message: data.message || prev.message,
      new_count: data.new_count,
      server_info: data.server_name
        ? {
            server_id: data.server_id ?? selectedServerId.value,
            server_name: data.server_name,
            location: serverOptions.value.find((s) => s.id === selectedServerId.value)?.location ?? '',
            country_code: '',
            flag_code: '',
            protocols_count: data.new_count ?? 0,
          }
        : prev.server_info,
      expiry_status_text: data.expiry_status_text ?? prev.expiry_status_text,
    }
    keysSyncing.value = true
    await keys.fetch(true)
  } catch (e) {
    pickError.value = extractError(e, 'Не удалось создать ключи')
  } finally {
    pickSubmitting.value = false
    keysSyncing.value = false
  }
}

async function submitRenewal() {
  const keyCode = result.value?.activated_key_code || code.value.trim()
  if (!keyCode || selectedCandidateKeyId.value == null) return
  pickSubmitting.value = true
  pickError.value = ''
  try {
    const { data } = await api.post<ActivationResponse>('/api/user/renewal-activation', {
      key_code: keyCode,
      key_id: selectedCandidateKeyId.value,
    })
    if (!data.success) {
      pickError.value = data.message || 'Не удалось продлить ключ'
      return
    }
    renewalDone.value = true
    wizardStep.value = 0
    const cand = selectedCandidate.value
    const prev = result.value
    result.value = {
      success: true,
      renewal_pick: false,
      message: data.message || prev?.message,
      duration_days: data.duration_days ?? prev?.duration_days,
      activated_key_code: prev?.activated_key_code,
      new_expires_at: data.new_expires_at ?? null,
      expiry_status_text: data.new_expires_at ?? prev?.expiry_status_text,
      extended_keys_count: data.extended_keys_count,
      server_info: cand
        ? {
            server_id: cand.key_id,
            server_name: cand.server_name,
            location: cand.location,
            country_code: cand.country_code ?? '',
            flag_code: cand.flag_code ?? '',
            protocols_count: cand.protocols_count ?? 0,
          }
        : prev?.server_info,
    }
    keysSyncing.value = true
    await keys.fetch(true)
  } catch (e) {
    pickError.value = extractError(e, 'Не удалось продлить ключ')
  } finally {
    pickSubmitting.value = false
    keysSyncing.value = false
  }
}

function reset() {
  code.value = ''
  status.value = 'idle'
  message.value = ''
  result.value = null
  modalOpen.value = false
  pickDone.value = false
  renewalDone.value = false
  serverOptions.value = []
  selectedServerId.value = null
  selectedCandidateKeyId.value = null
  pickError.value = ''
  wizardStep.value = 0
}

async function goToKeys() {
  if (keysSyncing.value) {
    try {
      await keys.fetch(true)
    } catch {
      /* ignore */
    }
  }
  modalOpen.value = false
  router.push('/')
}

function serverFlagUrl(s: PickServerOption): string {
  return flagSvgUrl(s.country_code ?? '', s.flag_code ?? '', s.location)
}

function candidateFlagUrl(c: RenewalCandidate): string {
  return flagSvgUrl(c.country_code ?? '', c.flag_code ?? '', c.location)
}

function formatExpires(raw?: string | null): string {
  if (!raw) return '—'
  const d = new Date(raw.replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return raw
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
</script>

<template>
  <div class="mx-auto max-w-xl">
    <h1 class="page-title mb-6">{{ $t('activate.title') }}</h1>

    <div class="panel">
      <form @submit.prevent="submit" class="flex flex-col gap-4">
        <label for="activation-code" class="text-sm text-muted">
          {{ $t('activate.placeholder') }}
        </label>
        <input
          id="activation-code"
          v-model="code"
          type="text"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          :placeholder="$t('activate.placeholder')"
          :disabled="status === 'processing'"
          class="form-input font-mono"
        />
        <button
          type="submit"
          class="btn btn-primary w-full"
          :disabled="status === 'processing' || !code.trim()"
        >
          <span v-if="status === 'processing'" class="inline-flex items-center gap-2">
            <span class="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            {{ $t('activate.processing') }}
          </span>
          <span v-else>{{ $t('activate.submit') }}</span>
        </button>

        <div v-if="status === 'error'" class="alert alert-danger">
          {{ message }}
        </div>
      </form>
    </div>

    <Modal :open="modalOpen && !!result" :max-width="needsPickServer ? 'lg' : 'md'" :show-close="true" @close="modalOpen = false">
      <template #header>
        <div class="flex min-w-0 flex-1 items-start gap-3 pr-3">
          <div
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
            :class="
              needsPickServer
                ? 'bg-primary-light text-primary'
                : isResume
                  ? 'bg-warning-light text-warning'
                  : 'bg-success-light text-success'
            "
          >
            <svg
              v-if="needsPickServer"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              class="h-6 w-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
              />
            </svg>
            <svg
              v-else-if="!isResume"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2.5"
              stroke="currentColor"
              class="h-6 w-6"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2.5"
              stroke="currentColor"
              class="h-6 w-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <h2 class="text-lg font-bold leading-snug">
              {{ modalTitle }}
            </h2>
            <p v-if="result?.message" class="mt-1 text-sm text-muted break-words">
              {{
                needsRenewalPick
                  ? wizardStep === 2
                    ? $t('activate.modal.renewal_confirm_hint')
                    : $t('activate.modal.renewal_pick_hint')
                  : needsPickServer
                    ? wizardStep === 2
                      ? $t('activate.modal.confirm_hint')
                      : $t('activate.modal.pick_hint')
                    : result.message
              }}
            </p>
            <span
              v-if="needsRenewalPick"
              class="mt-1 inline-flex items-center gap-1 rounded bg-primary-light/40 px-2 py-0.5 text-[0.7rem] font-medium text-primary"
            >
              {{ $t('activate.modal.renewal_badge') }}
            </span>
            <span
              v-else-if="needsPickServer"
              class="mt-1 inline-flex items-center gap-1 rounded bg-primary-light/40 px-2 py-0.5 text-[0.7rem] font-medium text-primary"
            >
              {{ $t('activate.modal.free_badge') }}
            </span>
          </div>
        </div>
      </template>

      <div v-if="result" class="space-y-4 text-sm">
        <div v-if="needsWizard" class="space-y-4">
          <!-- Progress steps -->
          <ol class="flex items-center gap-2">
            <li
              v-for="(label, idx) in wizardSteps"
              :key="idx"
              class="flex flex-1 items-center gap-2"
            >
              <div class="flex items-center gap-2">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition"
                  :class="
                    idx < wizardStep
                      ? 'bg-success text-white'
                      : idx === wizardStep
                        ? 'bg-primary text-white'
                        : 'bg-black/10 text-muted'
                  "
                >
                  <svg
                    v-if="idx < wizardStep"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="3"
                    stroke="currentColor"
                    class="h-4 w-4"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                  <span v-else>{{ idx + 1 }}</span>
                </span>
                <span
                  class="hidden text-xs font-medium sm:inline"
                  :class="idx === wizardStep ? 'text-primary' : 'text-muted'"
                >
                  {{ label }}
                </span>
              </div>
              <span
                v-if="idx < wizardSteps.length - 1"
                class="h-px flex-1"
                :class="idx < wizardStep ? 'bg-success' : 'bg-black/10'"
              />
            </li>
          </ol>

          <!-- Renewal step 1 -->
          <div v-if="needsRenewalPick && wizardStep === 1" class="space-y-3">
            <p class="text-xs text-muted">{{ $t('activate.modal.renewal_pick_hint') }}</p>
            <div v-if="result.duration_days" class="text-xs text-muted">
              {{ $t('activate.modal.renewal_days') }}:
              <span class="font-semibold text-dark">
                {{ $t('activate.modal.days_short', { n: result.duration_days }) }}
              </span>
            </div>
            <div v-if="renewalCandidates.length" class="space-y-2">
              <div class="text-xs text-muted">{{ $t('activate.modal.select_prompt') }}</div>
              <div class="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                <button
                  v-for="c in renewalCandidates"
                  :key="c.key_id"
                  type="button"
                  class="relative flex items-center gap-3 rounded-lg border p-3 text-left transition"
                  :class="
                    selectedCandidateKeyId === c.key_id
                      ? 'border-success bg-success-light/40 ring-2 ring-success'
                      : 'border-white-light hover:border-primary hover:bg-primary-light/20'
                  "
                  @click="selectCandidate(c.key_id)"
                >
                  <img
                    v-if="candidateFlagUrl(c)"
                    :src="candidateFlagUrl(c)"
                    :alt="c.country_code || ''"
                    class="h-auto w-6 shrink-0 rounded-sm"
                    loading="lazy"
                  />
                  <span class="min-w-0 flex-1">
                    <span class="block truncate font-semibold">{{ c.server_name }}</span>
                    <span class="block truncate text-xs text-muted">{{ c.location }}</span>
                    <span class="mt-1 block truncate text-[0.65rem] text-muted">
                      {{ $t('activate.modal.renewal_expires') }}: {{ formatExpires(c.expires_at) }}
                    </span>
                  </span>
                  <span
                    v-if="selectedCandidateKeyId === c.key_id"
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success text-white"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="3"
                      stroke="currentColor"
                      class="h-4 w-4"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
            <div v-else class="alert alert-danger">{{ $t('activate.modal.no_renewal_servers') }}</div>
            <div v-if="pickError" class="alert alert-danger">{{ pickError }}</div>
          </div>

          <!-- Renewal step 2 -->
          <div v-else-if="needsRenewalPick && wizardStep === 2" class="space-y-3">
            <p class="text-xs text-muted">{{ $t('activate.modal.renewal_confirm_hint') }}</p>
            <div
              v-if="selectedCandidate"
              class="flex items-center gap-3 rounded-lg border border-success bg-success-light/40 p-4"
            >
              <img
                v-if="candidateFlagUrl(selectedCandidate)"
                :src="candidateFlagUrl(selectedCandidate)"
                :alt="selectedCandidate.country_code || ''"
                class="h-auto w-7 shrink-0 rounded-sm"
                loading="lazy"
              />
              <div class="min-w-0 flex-1">
                <div class="text-xs uppercase tracking-wide text-muted">
                  {{ $t('activate.modal.confirm_server') }}
                </div>
                <div class="truncate text-base font-bold">{{ selectedCandidate.server_name }}</div>
                <div class="truncate text-xs text-muted">{{ selectedCandidate.location }}</div>
                <div class="mt-1 truncate text-[0.7rem] text-muted">
                  {{ $t('activate.modal.renewal_expires') }}:
                  {{ formatExpires(selectedCandidate.expires_at) }}
                </div>
              </div>
              <span class="badge badge-success">{{ $t('activate.modal.selected') }}</span>
            </div>
            <div v-if="result.duration_days" class="flex items-center justify-between gap-3">
              <span class="text-muted">{{ $t('activate.modal.renewal_days') }}</span>
              <span class="font-semibold">
                {{ $t('activate.modal.days_short', { n: result.duration_days }) }}
              </span>
            </div>
            <div v-if="pickError" class="alert alert-danger">{{ pickError }}</div>
          </div>

          <!-- Pick-server step 1 -->
          <div v-else-if="needsPickServer && wizardStep === 1" class="space-y-3">
            <p class="text-xs text-muted">{{ $t('activate.modal.free_hint') }}</p>

            <div v-if="serversLoading" class="py-4 text-center text-muted">
              {{ $t('dashboard.loading') }}
            </div>

            <div v-else-if="serverOptions.length" class="space-y-2">
              <div class="text-xs text-muted">{{ $t('activate.modal.select_prompt') }}</div>
              <div class="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                <button
                  v-for="s in serverOptions"
                  :key="s.id"
                  type="button"
                  class="relative flex items-center gap-3 rounded-lg border p-3 text-left transition"
                  :disabled="s.is_online === false"
                  :class="
                    s.is_online === false
                      ? 'cursor-not-allowed border-white-light opacity-55'
                      : selectedServerId === s.id
                        ? 'border-success bg-success-light/40 ring-2 ring-success'
                        : 'border-white-light hover:border-primary hover:bg-primary-light/20'
                  "
                  @click="selectServer(s.id)"
                >
                  <img
                    v-if="serverFlagUrl(s)"
                    :src="serverFlagUrl(s)"
                    :alt="s.country_code || ''"
                    class="h-auto w-6 shrink-0 rounded-sm"
                    loading="lazy"
                  />
                  <span class="min-w-0 flex-1">
                    <span class="block truncate font-semibold">{{ s.name }}</span>
                    <span class="block truncate text-xs text-muted">{{ s.location }}</span>
                    <span
                      class="mt-1 inline-block rounded px-1.5 py-0.5 text-[0.65rem] font-medium"
                      :class="
                        s.is_online === false
                          ? 'bg-danger-light text-danger'
                          : 'bg-success-light text-success'
                      "
                    >
                      {{ s.is_online === false ? $t('activate.modal.pick_offline') : $t('activate.modal.pick_online') }}
                    </span>
                    <span
                      v-if="s.protocols_text"
                      class="mt-1 block truncate text-[0.65rem] text-muted"
                    >
                      {{ s.protocols_text }}
                    </span>
                  </span>
                  <span
                    v-if="selectedServerId === s.id"
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success text-white"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="3"
                      stroke="currentColor"
                      class="h-4 w-4"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            <div v-if="pickError" class="alert alert-danger">{{ pickError }}</div>
          </div>

          <!-- Pick-server step 2 -->
          <div v-else-if="needsPickServer && wizardStep === 2" class="space-y-3">
            <p class="text-xs text-muted">{{ $t('activate.modal.confirm_hint') }}</p>
            <div
              v-if="selectedServer"
              class="flex items-center gap-3 rounded-lg border border-success bg-success-light/40 p-4"
            >
              <img
                v-if="serverFlagUrl(selectedServer)"
                :src="serverFlagUrl(selectedServer)"
                :alt="selectedServer.country_code || ''"
                class="h-auto w-7 shrink-0 rounded-sm"
                loading="lazy"
              />
              <div class="min-w-0 flex-1">
                <div class="text-xs uppercase tracking-wide text-muted">
                  {{ $t('activate.modal.confirm_server') }}
                </div>
                <div class="truncate text-base font-bold">{{ selectedServer.name }}</div>
                <div class="truncate text-xs text-muted">{{ selectedServer.location }}</div>
                <div
                  v-if="selectedServer.protocols_text"
                  class="mt-1 truncate text-[0.7rem] text-muted"
                >
                  {{ selectedServer.protocols_text }}
                </div>
              </div>
              <span class="badge badge-success">{{ $t('activate.modal.selected') }}</span>
            </div>

            <div v-if="result.expiry_status_text" class="flex items-center justify-between gap-3">
              <span class="text-muted">{{ $t('activate.modal.valid_until') }}</span>
              <span class="font-semibold">{{ result.expiry_status_text }}</span>
            </div>

            <div v-if="pickError" class="alert alert-danger">{{ pickError }}</div>
          </div>
        </div>

        <template v-else>
          <div v-if="displayServerInfo" class="space-y-2">
            <div class="flex items-center justify-between gap-3">
              <span class="text-muted">{{ $t('activate.modal.server') }}</span>
              <span class="flex items-center gap-2 text-right font-semibold">
                <img
                  v-if="flagUrl"
                  :src="flagUrl"
                  :alt="displayServerInfo.country_code || ''"
                  class="h-auto w-5 shrink-0 rounded-sm"
                  loading="lazy"
                />
                <span>{{ displayServerInfo.server_name }}</span>
              </span>
            </div>
            <div v-if="displayServerInfo.location || displayServerInfo.country_code" class="flex items-center justify-between gap-3">
              <span class="text-muted">{{ $t('activate.modal.location') }}</span>
              <LocationDisplay
                :country-code="displayServerInfo.country_code"
                :location="displayServerInfo.location"
                :flag-code="displayServerInfo.flag_code"
                :size="20"
              />
            </div>
            <div
              v-if="typeof displayServerInfo.protocols_count === 'number' && displayServerInfo.protocols_count > 0"
              class="flex items-center justify-between gap-3"
            >
              <span class="text-muted">{{ $t('activate.modal.protocols') }}</span>
              <span class="font-semibold">{{ displayServerInfo.protocols_count }}</span>
            </div>
          </div>

          <div
            v-if="
              (typeof result.new_count === 'number' && result.new_count > 0) ||
              (typeof result.updated_count === 'number' && result.updated_count > 0)
            "
            class="divider space-y-2 pt-3"
          >
            <div v-if="result.new_count && result.new_count > 0" class="flex items-center justify-between gap-3">
              <span class="text-muted">{{ $t('activate.modal.keys_created') }}</span>
              <span class="font-semibold text-success">{{ result.new_count }}</span>
            </div>
            <div
              v-if="result.updated_count && result.updated_count > 0"
              class="flex items-center justify-between gap-3"
            >
              <span class="text-muted">{{ $t('activate.modal.keys_updated') }}</span>
              <span class="font-semibold text-primary">{{ result.updated_count }}</span>
            </div>
          </div>

          <div v-if="result.expiry_status_text" class="divider pt-3">
            <div class="flex items-center justify-between gap-3">
              <span class="text-muted">{{ $t('activate.modal.valid_until') }}</span>
              <span class="text-right font-semibold">{{ result.expiry_status_text }}</span>
            </div>
          </div>
        </template>
      </div>

      <template #footer>
        <!-- Wizard step 1: choose server -->
        <template v-if="needsWizard && wizardStep === 1">
          <button type="button" class="btn btn-outline-danger" @click="reset">
            {{ $t('activate.modal.another_code') }}
          </button>
          <button
            type="button"
            class="btn btn-primary inline-flex items-center gap-2"
            :disabled="
              needsRenewalPick
                ? selectedCandidateKeyId == null
                : selectedServerId == null || serversLoading
            "
            @click="goNextStep"
          >
            {{ $t('activate.modal.next') }}
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="h-4 w-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </button>
        </template>

        <!-- Wizard step 2: confirm -->
        <template v-else-if="needsWizard && wizardStep === 2">
          <button type="button" class="btn btn-outline-primary" :disabled="pickSubmitting" @click="goPrevStep">
            {{ $t('activate.modal.back') }}
          </button>
          <button
            v-if="needsRenewalPick"
            type="button"
            class="btn btn-primary inline-flex items-center gap-2"
            :disabled="!canRenewalSubmit"
            @click="submitRenewal"
          >
            <span
              v-if="pickSubmitting"
              class="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
            />
            {{ pickSubmitting ? $t('activate.modal.renewal_processing') : $t('activate.modal.renewal_confirm_submit') }}
          </button>
          <button
            v-else
            type="button"
            class="btn btn-primary inline-flex items-center gap-2"
            :disabled="!canPickSubmit"
            @click="submitPickServer"
          >
            <span
              v-if="pickSubmitting"
              class="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
            />
            {{ pickSubmitting ? $t('activate.modal.pick_processing') : $t('activate.modal.confirm_create') }}
          </button>
        </template>

        <!-- Success / summary -->
        <template v-else>
          <button type="button" class="btn btn-outline-danger" @click="reset">
            {{ $t('activate.modal.another_code') }}
          </button>
          <button
            type="button"
            class="btn btn-primary inline-flex items-center gap-2"
            @click="goToKeys"
          >
            <span
              v-if="keysSyncing"
              class="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
            />
            {{ $t('activate.modal.go_to_keys') }}
          </button>
        </template>
      </template>
    </Modal>
  </div>
</template>
